// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { maxLengthAttr } from "@stanion/XDL/Utilities";
import { FormField, FormState } from "@stanion/XDL/Utilities/form";

export interface EventRegistrationState extends FormState {
    fields: {
        firstName: FormField<string>;
        lastName: FormField<string>;
        title: FormField<string>;
        company: FormField<string>;
        streetAddress1: FormField<string>;
        streetAddress2: FormField<string>;
        city: FormField<string>;
        country: FormField<string>;
        state: FormField<string>;
        postalCode: FormField<string>;
        companyPhone: FormField<string>;
        mobilePhone: FormField<string>;
        email: FormField<string>;
        poNumber: FormField<string>;
        comments: FormField<string>;
        howToContact: FormField<string>;
    };
}

export const initialEventRegistrationFormState: EventRegistrationState = {
    isValid: false,
    isSubmitted: false,
    fields: {
        firstName: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required"] : [];
            },
            attrs: {
                id: "eventRegistration_firstName",
                name: "firstName",
                label: translate("First Name"),
                placeholder: translate("First Name"),
                required: true,
            },
        },
        lastName: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required"] : [];
            },
            attrs: {
                id: "eventRegistration_lastName",
                name: "lastName",
                label: translate("Last Name"),
                placeholder: translate("Last Name"),
                required: true,
            },
        },
        title: {
            value: "",
            attrs: {
                id: "eventRegistration_title",
                name: "title",
                label: translate("Title"),
                placeholder: translate("Title"),
            },
        },
        company: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required"] : [];
            },
            attrs: {
                id: "eventRegistration_company",
                name: "company",
                label: translate("Company"),
                placeholder: translate("Company"),
                required: true,
            },
        },
        streetAddress1: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required"] : [];
            },
            attrs: {
                id: "eventRegistration_streetAddress1",
                name: "streetAddress1",
                label: translate("Street Address1"),
                placeholder: translate("Street Address1"),
                required: true,
                maxLength: maxLengthAttr.address,
            },
        },
        streetAddress2: {
            value: "",
            validators: [],
            attrs: {
                id: "eventRegistration_streetAddress2",
                name: "streetAddress2",
                label: translate("Address Cont’d"),
                placeholder: translate("Address Cont’d"),
                maxLength: maxLengthAttr.address,
            },
        },
        city: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required"] : [];
            },
            attrs: {
                id: "eventRegistration_city",
                name: "city",
                label: translate("City"),
                placeholder: translate("City"),
                required: true,
                maxLength: maxLengthAttr.city,
            },
        },
        country: {
            value: "",
            validators: [],
            attrs: {
                id: "eventregistration_country",
                name: "country",
                label: translate("Country"),
            },
        },
        state: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required"] : [];
            },
            attrs: {
                id: "eventRegistration_state",
                name: "state",
                label: translate("State"),
                placeholder: translate("State"),
                required: true,
            },
        },
        postalCode: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required", "zipCode"] : [];
            },
            attrs: {
                id: "eventRegistration_postalCode",
                name: "postalCode",
                label: translate("Zip/Postal Code"),
                placeholder: translate("Zip/Postal Code"),
                required: true,
                maxLength: maxLengthAttr.postalCode,
            },
        },
        companyPhone: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required", "phone"] : [];
            },
            attrs: {
                id: "eventRegistration_companyPhone",
                name: "companyPhone",
                label: translate("Company Main Phone"),
                placeholder: translate("Company Main Phone"),
                required: true,
                maxLength: maxLengthAttr.phone,
            },
        },
        mobilePhone: {
            value: "",
            validators: ["required", "phone"],
            attrs: {
                id: "eventRegistration_mobilePhone",
                name: "mobilePhone",
                label: translate("Contact/Mobile Phone"),
                placeholder: translate("Contact/Mobile Phone"),
                required: true,
                maxLength: maxLengthAttr.phone,
            },
        },
        email: {
            value: "",
            validators: (state, options) => {
                return !options?.isAuthenticated ? ["required", "email"] : [];
            },
            attrs: {
                id: "eventRegistration_email",
                name: "email",
                label: translate("Email address"),
                placeholder: translate("Email address"),
                required: true,
                maxLength: maxLengthAttr.email,
            },
        },
        poNumber: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "eventRegistration_poNumber",
                name: "poNumber",
                label: translate("Purchase Order #"),
                placeholder: translate("Purchase Order #"),
                required: true,
            },
        },
        comments: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "eventRegistration_comments",
                name: "comments",
                label: translate("Comments"),
                placeholder: translate("Comments"),
                required: true,
                maxLength: maxLengthAttr.note,
            },
        },
        howToContact: {
            value: "Additional Comments or instructions",
            validators: ["required"],
            attrs: {
                id: "eventRegistration_howToContact",
                name: "howToContact",
                label: translate("How can we contact you?"),
                placeholder: translate("How can we contact you?"),
                required: true,
            },
        },
    },
};
